import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import Auth from './pages/Auth';
import ResetPassword from './pages/ResetPassword';
import Landing from './pages/Landing';
import Discover from './pages/Discover';
import Overview from './pages/Overview';
import Projects from './pages/Projects';
import Tasks from './pages/Tasks';
import Schedule from './pages/Schedule';
import Team from './pages/Team';
import Reports from './pages/Reports';
import ProjectDetail from './pages/ProjectDetail';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Pricing from './pages/Pricing';
import './App.css';

function hasAccess(user) {
  if (!user) return false;
  if (user.subscription_bypass) return true;
  // Free users have access to dashboard (with limitations)
  if (user.account_type === 'free' || user.account_type === 'paid') return true;
  const status = user.subscription_status;
  return status === 'active' || status === 'trialing';
}

function AppRoutes({ currentUser, setCurrentUser, theme, toggleTheme, searchQuery, setSearchQuery, sidebarCollapsed, setSidebarCollapsed }) {
  const navigate = useNavigate();

  const handleLogin = (user) => {
    setCurrentUser(user);
    if (hasAccess(user)) {
      navigate('/');
    } else {
      navigate('/pricing');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    setCurrentUser(null);
    navigate('/');
  };

  // Not logged in: public routes
  if (!currentUser) {
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/decouvrir" element={<Discover />} />
        <Route path="/login" element={<Auth onLogin={handleLogin} />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Logged in but no access: pricing only
  if (!hasAccess(currentUser)) {
    return (
      <div className="min-h-screen bg-background">
        <Routes>
          <Route path="/pricing" element={<Pricing currentUser={currentUser} onLogout={handleLogout} />} />
          <Route path="*" element={<Navigate to="/pricing" replace />} />
        </Routes>
      </div>
    );
  }

  // Logged in + subscription: full dashboard
  return (
    <div className="flex h-screen overflow-hidden bg-background" data-testid="app-container">
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar
          theme={theme}
          onThemeToggle={toggleTheme}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          currentUser={currentUser}
          onLogout={handleLogout}
        />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="max-w-[1440px] mx-auto">
            <Routes>
              <Route path="/" element={<Overview />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/schedule" element={<Schedule />} />
              <Route path="/team" element={<Team />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/project/:projectId" element={<ProjectDetail />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/pricing" element={<Pricing currentUser={currentUser} onLogout={handleLogout} />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}

function App() {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'light';
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [currentUser, setCurrentUser] = useState(() => {
    const saved = localStorage.getItem('currentUser');
    return saved ? JSON.parse(saved) : null;
  });

  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarCollapsed(true);
      } else {
        setSidebarCollapsed(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <BrowserRouter>
      <AppRoutes
        currentUser={currentUser}
        setCurrentUser={setCurrentUser}
        theme={theme}
        toggleTheme={toggleTheme}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
      />
    </BrowserRouter>
  );
}

export default App;
